import { t } from '@app/i18n';
import { inSubtitle, truncar } from './accessMapVocab';
import {
  LABEL_LIMIT,
  SAME_COLUMN,
  NODE_ANEL,
  NODE_R,
  RECUO_DO_LACO,
  LABEL_DY,
  SUB_DY,
  type GraphLayout,
  type Point,
} from './useGraphLayout';

/**
 * **Caber na área** — o enquadramento inicial do grafo de T19 (29/08).
 *
 * O `viewBox` do SVG é a grade que `useGraphLayout` calcula, com folga à direita
 * e embaixo para os rótulos, e o desenho não a preenche: em 1920×1080 as colunas
 * ocupavam ~60 % da altura do palco e ficavam à esquerda do centro, porque o
 * `PAD_RIGHT` de 200 unidades entra na conta do `preserveAspectRatio` como se
 * fosse desenho. Aqui se mede o que está **realmente** desenhado e se calcula a
 * transformação que o põe centrado e do tamanho da seção.
 *
 * A transformação é a mesma que a roda e o arraste já usam (o `translate` +
 * `scale` do `<g>` do mundo), então enquadrar é só um ponto de partida: o
 * usuário continua por cima dela, e a partir do primeiro gesto o enquadramento
 * automático se cala (é o que «Ajustar à tela» refaz).
 *
 * **O que isto não faz:** preencher os dois eixos. A escala é uniforme — tem de
 * ser, senão os discos viram elipses —, e o desenho é largo e baixo (~2,4 : 1)
 * contra uma seção quase 1,5 : 1. Enquadrar leva o eixo limitante a 90 % e o
 * outro fica onde a proporção o deixar. Encher a altura exigiria espalhar os nós
 * verticalmente, que é mudar o grafo, não a moldura.
 */

export const ZOOM_MIN = 0.3;
export const ZOOM_MAX = 4;

/** Quanto da seção o desenho ocupa no eixo limitante. */
export const MARGEM_DEFAULT = 0.9;

/**
 * Largura média de caractere, em unidades do `viewBox`, por papel de texto.
 *
 * É estimativa, e de propósito: medir de verdade seria `getComputedTextLength`,
 * que não existe no jsdom (o enquadramento ficaria sem teste) e que obrigaria a
 * enquadrar **depois** de pintar. O erro de uma estimativa aqui custa alguns
 * pixels de folga; a margem de 10 % o cobre com sobra.
 *
 * Os valores são a caixa média das fontes do tema nos tamanhos de cada papel:
 * 12 px/600 no rótulo, 10,5 px no subtítulo, 11 px/700 com `letter-spacing:
 * 0.1em` maiúsculo no cabeçalho de coluna.
 */
const WIDTH_BY_CARACTERE = { rotulo: 6.6, sub: 5.4, coluna: 8.2 };

/** Descida da fonte abaixo da linha de base, nos dois tamanhos de rótulo. */
const DESCIDA = 3;
/** Cabeçalho de coluna: linha de base em `y = 34` (`Graph.tsx`), 11 px de altura. */
const COLUMN_Y = 34;
const COLUMN_HEIGHT = 11;

export interface Retangulo {
  x: number;
  y: number;
  largura: number;
  altura: number;
}

export interface Transformacao {
  zoom: number;
  pan: Point;
}

/** Sem transformação nenhuma — o que se usa quando não há o que enquadrar. */
export const WITHOUT_SETTING: Transformacao = { zoom: 1, pan: { x: 0, y: 0 } };

/**
 * Retângulo do que está desenhado, em unidades do `viewBox`: discos com o anel,
 * os dois rótulos de cada nó, os cabeçalhos de coluna e o laço das arestas da
 * mesma coluna (que sai à esquerda e é a única parte do traço que pode passar
 * dos nós). `null` quando não há nó — aí não há o que enquadrar.
 */
export function areaDoDesenho(layout: GraphLayout): Retangulo | null {
  if (layout.nos.length === 0) return null;

  let x0 = Number.POSITIVE_INFINITY;
  let y0 = Number.POSITIVE_INFINITY;
  let x1 = Number.NEGATIVE_INFINITY;
  let y1 = Number.NEGATIVE_INFINITY;
  const include = (x: number, y: number) => {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  };
  /** Rótulo centrado no disco: transborda metade da largura para cada lado. */
  const texto = (centroX: number, base: number, content: string, porCaractere: number) => {
    const meia = (truncar(content, LABEL_LIMIT).length * porCaractere) / 2;
    include(centroX - meia, base + DESCIDA);
    include(centroX + meia, base + DESCIDA);
  };

  for (const item of layout.nos) {
    const raio = NODE_R + NODE_ANEL;
    include(item.x - raio, item.y - raio);
    include(item.x + raio, item.y + raio);
    texto(item.x, item.y + LABEL_DY, item.no.label, WIDTH_BY_CARACTERE.rotulo);
    texto(item.x, item.y + SUB_DY, inSubtitle(item.no), WIDTH_BY_CARACTERE.sub);
  }

  for (const coluna of layout.colunas) {
    // O rótulo desenhado, e não a chave: a moldura tem de caber no TEXTO, e o
    // comprimento dele muda com o idioma («Recursos» × «Resources»).
    const meia = (t(coluna.rotuloKey).length * WIDTH_BY_CARACTERE.coluna) / 2;
    include(coluna.x - meia, COLUMN_Y - COLUMN_HEIGHT);
    include(coluna.x + meia, COLUMN_Y);
  }

  for (const item of layout.arestas) {
    if (Math.abs(item.de.x - item.para.x) >= SAME_COLUMN) continue;
    include(Math.min(item.de.x, item.para.x) - RECUO_DO_LACO, item.de.y);
  }

  return { x: x0, y: y0, largura: x1 - x0, altura: y1 - y0 };
}

/**
 * Transformação que põe `alvo` centrado na seção e ocupando `margem` do eixo
 * limitante, sem distorcer.
 *
 * As três caixas em jogo:
 *
 * - `alvo` e `viewBox` estão em **unidades do viewBox** — é nelas que o `<g>`
 *   do mundo aplica `translate(pan) scale(zoom)`;
 * - `elemento` é a seção em **pixels de tela**.
 *
 * Entre as duas há a escala que o próprio SVG aplica (`preserveAspectRatio=
 * "xMidYMid meet"`): `k = min(elW/vbW, elH/vbH)`, com o `viewBox` centrado no
 * elemento. Então a escala total na tela é `k × zoom`, e é ela que a margem
 * governa — daí o `total / k`. E como o `xMidYMid` faz o centro do elemento
 * coincidir com o centro do `viewBox`, centrar o desenho é levar o centro de
 * `alvo` ao centro do `viewBox`.
 */
export function caberNaArea(
  alvo: Retangulo,
  viewBox: { largura: number; altura: number },
  elemento: { largura: number; altura: number },
  margem: number = MARGEM_DEFAULT,
): Transformacao {
  const medidas = [
    alvo.largura,
    alvo.altura,
    viewBox.largura,
    viewBox.altura,
    elemento.largura,
    elemento.altura,
  ];
  // Seção ainda sem caixa (montagem, aba fechada) ou desenho degenerado: sem
  // enquadramento é melhor do que com um `NaN` no `transform`, que apaga o mapa.
  if (medidas.some((valor) => !Number.isFinite(valor) || valor <= 0)) return WITHOUT_SETTING;

  const k = Math.min(elemento.largura / viewBox.largura, elemento.altura / viewBox.altura);
  const total = margem * Math.min(elemento.largura / alvo.largura, elemento.altura / alvo.altura);
  const zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, total / k));

  return {
    zoom,
    pan: {
      x: viewBox.largura / 2 - zoom * (alvo.x + alvo.largura / 2),
      y: viewBox.altura / 2 - zoom * (alvo.y + alvo.altura / 2),
    },
  };
}
